import cn from 'classnames';
import styles from './MultiRangeSlider.module.scss';
import { ChangeEvent, useCallback, useEffect, useRef } from 'react';

type MultiRangeSliderProps = {
  className?: string;
  values: {
    max: number;
    min: number;
    chosenMax: number;
    chosenMin: number;
  };
  onChangeMin: (value: number) => void;
  onChangeMax: (value: number) => void;
  onFinalChange?: () => void;
};

export function MultiRangeSlider({
  className,
  values,
  onChangeMax,
  onChangeMin,
  onFinalChange,
}: MultiRangeSliderProps) {
  const wrapperClassName = cn(styles.wrapper, className);
  const { min, max, chosenMin, chosenMax } = values;

  const minValRef = useRef(min);
  const maxValRef = useRef(max);
  const rangeRef = useRef<HTMLDivElement>(null);

  const getPercent = useCallback(
    (value: number) => Math.round(((value - min) / (max - min)) * 100),
    [min, max],
  );

  useEffect(() => {
    const minPercent = getPercent(chosenMin);
    const maxPercent = getPercent(chosenMax);

    if (rangeRef.current) {
      rangeRef.current.style.left = `${minPercent}%`;
      rangeRef.current.style.width = `${maxPercent - minPercent}%`;
    }
  }, [chosenMin, chosenMax, getPercent]);

  const handleMouseUp = () => {
    onFinalChange?.();
  };

  return (
    <div className={wrapperClassName}>
      <input
        type="range"
        min={min}
        max={max}
        value={chosenMin}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = Math.min(Number(event.target.value), chosenMax - 1);
          onChangeMin(value);
          minValRef.current = value;
        }}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        className={cn(styles.thumb, styles.thumbLeft)}
      />
      <input
        type="range"
        min={min}
        max={max}
        value={chosenMax}
        onChange={(event: ChangeEvent<HTMLInputElement>) => {
          const value = Math.max(Number(event.target.value), chosenMin + 1);
          onChangeMax(value);
          maxValRef.current = value;
        }}
        onMouseUp={handleMouseUp}
        onTouchEnd={handleMouseUp}
        className={cn(styles.thumb, styles.thumbRight)}
      />

      <div className={styles.slider}>
        <div className={styles.sliderTrack}></div>
        <div ref={rangeRef} className={styles.sliderRange}></div>
      </div>
    </div>
  );
}
